<template>
    <div class="country-language-selection__content" :data-testid="props.dataTestid">
        <div class="country-language-selection__content__item">
            <VJoySelect
                :model-value="selectedCountryCode"
                :label="props.countryLabel"
                :data-testid="`${props.dataTestid}--country-select`"
                @update:model-value="handleCountryUpdate"
            >
                <option v-for="host in supportedHosts" :key="`country-${host.countryCode}`" :value="host.countryCode">
                    {{ host.countryName }}
                </option>
            </VJoySelect>
        </div>
        <div class="country-language-selection__content__item">
            <VJoySelect
                v-model="selectedLanguageCode"
                :label="props.languageLabel"
                :data-testid="`${props.dataTestid}--language-select`"
                @update:model-value="handleLanguageUpdate"
            >
                <template v-if="selectedHost">
                    <option v-for="language in selectedHost.languages" :key="`language-${language.language}`" :value="language.language">
                        {{ formatLanguageName(language) }}
                    </option>
                </template>
            </VJoySelect>
        </div>
    </div>
</template>

<script setup lang="ts">
    import {computed, ref} from 'vue';
    import {useAsyncData, useFormat, useHosts, useLocale, useRequestURL, useRuntimeConfig, useTranslation, type Host, type Language} from '#imports';
    import type {LocalePrefixConfig} from '@malt/nuxt-i18n-module/types';
    import {VJoySelect} from '@maltjoy/core-vue';
    import {findHostAndLanguageForLocale} from '../utils/localeSelection';

    const props = defineProps<{
        dataTestid: string;
        countryLabel: string;
        languageLabel: string;
        currentLocale: string;
    }>();

    type CountryAndLanguageSelectionUpdateEvent = {
        maybeSelectedLocale: string | undefined;
        maybeSelectedHost: Host | undefined;
        maybeSelectedLanguage: Language | undefined;
    };

    const emit = defineEmits<{
        'update:countryCode': [countryCode: string, maybeSelectedInfos: CountryAndLanguageSelectionUpdateEvent];
        'update:languageCode': [languageCode: string, maybeSelectedInfos: CountryAndLanguageSelectionUpdateEvent];
    }>();
    // Composables
    const {getHosts} = useHosts();
    const {isMultiLocaleHost: isMaltCom} = useLocale();
    const {formatCountryName, formatLanguageName} = useFormat();
    const {t} = useTranslation();
    const {host: currentHost} = useRequestURL();
    const supportedLocalePrefixes = (useRuntimeConfig().public['@malt/nuxt-i18n-module']?.supportedLocalePrefixes ?? []) as LocalePrefixConfig[];

    // Refs
    const selectedHost = ref<Host | undefined>();
    const selectedCountryCode = ref('');
    const selectedLanguageCode = ref('');

    // A regionalized locale is a language and a country: de-CH is German in Switzerland.
    function hostsFromSupportedLocalePrefixes(localePrefixes: LocalePrefixConfig[], languageHost: string): Host[] {
        const hostsByCountry = new Map<string, Host>();

        for (const {code, redirectionActive} of localePrefixes) {
            if (redirectionActive === false) {
                continue;
            }

            const parts = code.split('-');
            if (parts.length !== 2 || !parts[0] || !parts[1]) {
                continue;
            }

            const [languageCode, countryCode] = parts;
            const normalizedCountryCode = countryCode.toUpperCase();
            const countryHost = hostsByCountry.get(normalizedCountryCode) ?? {
                countryCode: normalizedCountryCode,
                countryName: normalizedCountryCode,
                languages: [],
            };

            countryHost.languages.push({
                host: languageHost,
                language: languageCode.toLowerCase(),
                locale: code,
                label: languageCode,
            });
            hostsByCountry.set(normalizedCountryCode, countryHost);
        }

        return [...hostsByCountry.values()];
    }

    function mergeHostsByCountryCode(apiHosts: Host[], prefixHosts: Host[]): Host[] {
        const hostsByCountry = new Map(apiHosts.map((host) => [host.countryCode, host]));

        for (const prefixHost of prefixHosts) {
            hostsByCountry.set(prefixHost.countryCode, prefixHost);
        }

        return [...hostsByCountry.values()];
    }

    const {data} = await useAsyncData(
        'country-and-language-selection',
        async () => {
            const apiHosts = await getHosts();
            const prefixHosts = hostsFromSupportedLocalePrefixes(supportedLocalePrefixes, currentHost);
            const hosts = mergeHostsByCountryCode(apiHosts, prefixHosts);

            // Filter out malt.com
            const filteredHostsWithoutMaltCom = hosts.filter((host) => !!host.countryName && !!host.countryCode);

            const translatedHosts = filteredHostsWithoutMaltCom.map((host) => ({
                ...host,
                countryName: formatCountryName(host),
            }));
            const sortedHosts = translatedHosts.sort((a, b) => a.countryName.localeCompare(b.countryName));

            if (isMaltCom.value) {
                // Isolate malt.com (or its fallback)
                const maltComHost = hosts.find((host) => !host.countryName && !host.countryCode) ?? {
                    countryName: '',
                    countryCode: '',
                    languages: [
                        {
                            host: 'malt.com',
                            language: 'en',
                            locale: 'en',
                            label: 'English',
                        },
                    ],
                };
                maltComHost.countryName = t('navbar.feat.locale-selection.dialog.malt-com.country.select.option');
                sortedHosts.push(maltComHost);
            }

            return {sortedHosts};
        },
        {deep: true},
    );

    applyCurrentLocaleSelection(data.value?.sortedHosts ?? []);

    // Computed values
    const supportedHosts = computed<Host[]>(() => data.value?.sortedHosts ?? []);
    const maybeSelectedLanguage = computed<Language | undefined>(() =>
        selectedHost.value?.languages.find((language) => language.language === selectedLanguageCode.value),
    );
    const maybeSelectedLocale = computed<string | undefined>(() => maybeSelectedLanguage.value?.locale);

    function applyCurrentLocaleSelection(hosts: Host[]) {
        const {host, language} = findHostAndLanguageForLocale(hosts, props.currentLocale);
        selectedHost.value = host;
        selectedCountryCode.value = host?.countryCode ?? '';
        selectedLanguageCode.value = language?.language ?? '';
    }

    // Handlers
    function handleCountryUpdate(countryCode: string) {
        selectedCountryCode.value = countryCode;
        selectedHost.value = supportedHosts.value.find((host) => host.countryCode === countryCode);

        if (selectedHost.value !== undefined) {
            selectedLanguageCode.value =
                selectedHost.value.languages.find((language) => language.language === selectedLanguageCode.value)?.language ??
                selectedHost.value.languages[0]?.language ??
                '';
        }

        emit('update:countryCode', countryCode, {
            maybeSelectedLocale: maybeSelectedLocale.value,
            maybeSelectedHost: selectedHost.value,
            maybeSelectedLanguage: maybeSelectedLanguage.value,
        });
    }

    function handleLanguageUpdate(languageCode: string) {
        selectedLanguageCode.value = languageCode;

        emit('update:languageCode', languageCode, {
            maybeSelectedLocale: maybeSelectedLocale.value,
            maybeSelectedHost: selectedHost.value,
            maybeSelectedLanguage: maybeSelectedLanguage.value,
        });
    }
</script>
<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .country-language-selection__content {
        display: flex;
        flex-direction: column;
        gap: var(--joy-core-spacing-6);
        justify-content: stretch;

        &__item {
            width: 100%;
            flex: 1;
        }

        @include mq.screen-sm(min) {
            flex-direction: row;

            &__item {
                width: 50%;
            }
        }
    }
</style>
